/**
 * Detailer: scroll reveal.
 *
 * Fades each section in and lifts it into place as it enters the viewport.
 *
 * FOUR THINGS THIS DELIBERATELY GETS RIGHT
 *
 * 1. It hides nothing until it is certain it can un-hide it. The `opacity: 0`
 *    rules in assets/detailer-reveal.css are gated behind `.dtl-reveal-enabled`
 *    on <html>, which is added here. A blocked, failed or thrown script leaves
 *    the page exactly as it renders today rather than blanking every section.
 *
 * 2. Only sections BELOW the fold are touched at all. Anything already on
 *    screen at init is left entirely alone - never marked, never hidden,
 *    never transformed - so there is no flash of hidden content on load and
 *    the hero never fades in under the header.
 *
 * 3. The transform is removed once it has served its purpose. A `translate`
 *    other than `none` makes an element a containing block for fixed and
 *    absolutely positioned descendants, which silently breaks `position:
 *    sticky` and `position: fixed` inside that section. Leaving every section
 *    permanently transformed would be a real regression for a subtle effect,
 *    so each one is cleaned up when its transition ends.
 *
 * 4. Chrome, the cart drawer and the announcement bar are skipped. They are not
 *    part of the scroll narrative, and animating the header would fight
 *    assets/detailer-header-phase.js, which samples what sits behind it.
 */

const ENABLED_CLASS = 'dtl-reveal-enabled';
const MARK_ATTRIBUTE = 'data-dtl-reveal';
const REVEALED_CLASS = 'dtl-revealed';
const DONE_CLASS = 'dtl-reveal-done';

/**
 * Sections that must never be hidden or transformed. Matched against the
 * section element's id, which Shopify renders as `shopify-section-<name>`.
 */
const SKIP_PATTERN = /header|announcement|cart-drawer|utilities|footer/i;

/** How far into the viewport a section must come before it reveals. */
const OBSERVER_OPTIONS = {
  rootMargin: '0px 0px -10% 0px',
  threshold: 0.04,
};

/** @type {IntersectionObserver | null} */
let observer = null;

/* ---- Footer ------------------------------------------------------------
 * The footer gets a different reveal from everything else: rather than the
 * whole slab fading up, its columns drift down and un-blur one after another.
 * Ported from the 21st.dev footer-section component.
 * ---------------------------------------------------------------------- */

const FOOTER_SELECTOR = '.footer-content';
const FOOTER_MARK = 'data-dtl-footer';
const FOOTER_IN_CLASS = 'dtl-footer-in';
const FOOTER_DONE_CLASS = 'dtl-footer-done';

/** Longest column: 400ms delay + 800ms duration, plus a margin. */
const FOOTER_SETTLE_MS = 1600;

/** @type {IntersectionObserver | null} */
let footerObserver = null;

/**
 * Clears the effect, and with it the `filter` and `translate`. Both make an
 * element a containing block for fixed and absolutely positioned descendants,
 * the same hazard the section reveal cleans up after.
 * @param {HTMLElement} container
 */
function settleFooter(container) {
  container.classList.add(FOOTER_DONE_CLASS);
  container.classList.remove(FOOTER_IN_CLASS);
  container.removeAttribute(FOOTER_MARK);
  container.classList.remove(FOOTER_DONE_CLASS);
}

/**
 * @param {HTMLElement} container
 */
function registerFooter(container) {
  if (container.hasAttribute(FOOTER_MARK)) return;
  // Same rule as sections: anything already on screen is never touched, so a
  // short page can never flash its footer.
  if (container.getBoundingClientRect().top < window.innerHeight) return;
  container.setAttribute(FOOTER_MARK, '');
  footerObserver?.observe(container);
}

/**
 * Strips every trace of the effect from a section: the marker attribute, the
 * state classes, and with them the transform. Safe to call more than once.
 * @param {HTMLElement} section
 */
function settle(section) {
  section.classList.add(DONE_CLASS);
  section.classList.remove(REVEALED_CLASS);
  section.removeAttribute(MARK_ATTRIBUTE);
  section.classList.remove(DONE_CLASS);
  section.style.removeProperty('will-change');
}

/**
 * @param {HTMLElement} section
 */
function reveal(section) {
  section.classList.add(REVEALED_CLASS);

  // `transitionend` fires once per animated property, so the listener is bound
  // `once` against opacity specifically and backed by a timer in case the
  // transition never runs (the tab was hidden, reduced motion is on, the
  // element was removed mid-flight).
  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    settle(section);
  };

  section.addEventListener(
    'transitionend',
    (event) => {
      if (event.propertyName === 'opacity') finish();
    },
    { once: false }
  );

  setTimeout(finish, 1400);
}

/**
 * @param {HTMLElement} section
 */
function register(section) {
  if (SKIP_PATTERN.test(section.id)) return;
  if (section.hasAttribute(MARK_ATTRIBUTE)) return;

  const { top } = section.getBoundingClientRect();

  // Already on screen: leave it completely alone. Marking it and then
  // un-marking it on the next frame would be busywork with a real failure
  // mode - requestAnimationFrame does not fire in a tab that is not
  // compositing (backgrounded, or a hidden preview pane), which would strand
  // the section holding a `translate`. A translate other than `none` makes
  // the element a containing block for fixed and absolutely positioned
  // descendants, quietly breaking `position: sticky` inside it. Never
  // applying it is strictly safer than applying and cleaning up.
  if (top < window.innerHeight) return;

  section.setAttribute(MARK_ATTRIBUTE, '');
  section.style.setProperty('will-change', 'opacity, translate');
  observer?.observe(section);
}

function init() {
  // No IntersectionObserver means no way to know when to un-hide, so the
  // effect is declined outright rather than risking hidden content.
  if (!('IntersectionObserver' in window)) return;

  document.documentElement.classList.add(ENABLED_CLASS);

  observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const section = /** @type {HTMLElement} */ (entry.target);
      observer?.unobserve(section);
      reveal(section);
    }
  }, OBSERVER_OPTIONS);

  document.querySelectorAll('[id^="shopify-section-"]').forEach((section) => {
    if (section instanceof HTMLElement) register(section);
  });

  footerObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const container = /** @type {HTMLElement} */ (entry.target);
      footerObserver?.unobserve(container);
      container.classList.add(FOOTER_IN_CLASS);
      setTimeout(() => settleFooter(container), FOOTER_SETTLE_MS);
    }
  }, OBSERVER_OPTIONS);

  document.querySelectorAll(FOOTER_SELECTOR).forEach((container) => {
    if (container instanceof HTMLElement) registerFooter(container);
  });
}

// The theme editor re-renders a section in place; it arrives un-marked and
// mid-viewport, so it is re-registered and will simply reveal immediately.
document.addEventListener('shopify:section:load', (event) => {
  const target = /** @type {HTMLElement | null} */ (
    /** @type {CustomEvent} */ (event).target instanceof HTMLElement ? event.target : null
  );
  if (!target || !observer) return;
  const section = target.id?.startsWith('shopify-section-') ? target : target.closest('[id^="shopify-section-"]');
  if (section instanceof HTMLElement) register(section);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
